import axios, { AxiosResponse } from "axios";
import { WebAPI, IFailCallbackParam } from "../constants";
import { getErrorStatus } from "./getErrorStatus";
import AuthService from "../../common/AuthService";

interface ICallbackParams extends IFailCallbackParam {
  /**
   * WebAPIからモデル学習時の学習設定取得が成功した場合に呼び出されます
   * @callback success
   * @param {IGetFkdppconfResponceModel} fkdppconf - 学習設定
   * @param {number} status - httpステータス
   */
  success: (fkdppconf: IGetFkdppconfResponceModel, status: number) => void;
}

export interface IGetFkdppconfResponceModel {
  /**
   * PVタグ一覧
   */
  pvtag: string[];
  /**
   * PID制御周期[msec]（学習設定がPID直接制御の場合のみ）
   */
  pidinterval?: number;
  /**
   * FKDPP制御周期[msec]
   */
  fkdppinterval: number;
}

function AxiosGet(url: string, token?: string) {
  if (token) {
    return axios.get(url, {
      headers: {
        Authorization: "Bearer " + token,
      },
    });
  } else {
    return axios.get(url);
  }
}

interface IAxiosInternalParams extends ICallbackParams {
  promise: Promise<AxiosResponse>;
}

function AxiosInternal(params: IAxiosInternalParams) {
  params.promise
    .then((res) => {
      const data: IGetFkdppconfResponceModel = res.data;
      params.success(data, res.status);
    })
    .catch((err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    });
}

export interface IGetFkdppconfParams extends ICallbackParams {
  /**
   * 認証オブジェクト
   */
  authService?: AuthService;
  /**
   * プロジェクトID
   */
  projectid: string;
  /**
   * モデルID
   */
  modelid: string;
}

/**
 * モデル学習時の学習設定を取得します
 * @function getFkdppconf
 * @param {IGetFkdppconfParams} params - 学習設定取得パラメータ
 */
export function getFkdppconf(params: IGetFkdppconfParams): void {
  const { authService, projectid, modelid } = params;
  const url = WebAPI.GETFKDPPCONF_MODEL(projectid, modelid);
  if (authService) {
    authService.getToken({
      success: (token) => {
        AxiosInternal({
          promise: AxiosGet(url, token),
          success: params.success,
          fail: (id, status) => {
            if (status === 401) {
              getFkdppconfRetry401(authService, url, params);
            } else {
              params.fail(id, status);
            }
          },
        });
      },
      fail: (err) => {
        const { id, status } = getErrorStatus(err);
        params.fail(id, status);
      },
    });
  } else {
    AxiosInternal({
      promise: AxiosGet(url),
      success: params.success,
      fail: params.fail
    });
  }
}

/**
 * モデル学習時の学習設定を取得します
 * @function getFkdppconfRetry401
 * @param {AuthService} authService - 認証オブジェクト
 * @param {string} url - リクエストURL
 * @param {ICallbackParams} params - コールバックパラメータ
 */
function getFkdppconfRetry401(
  authService: AuthService,
  url: string,
  params: ICallbackParams
): void {
  authService.updateToken({
    success: (token) => {
      AxiosInternal({
        promise: AxiosGet(url, token),
        success: params.success,
        fail: params.fail,
      });
    },
    fail: (err) => {
      const { id, status } = getErrorStatus(err);
      params.fail(id, status);
    },
  });
}